import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataTable } from '../../../shared/hooks/DataTable';
import { BankService as Service } from './BankService';
import { useDialogCrup } from '../../../shared/hooks/DialogCrup';
import { useToolbar } from '../../../shared/hooks/Toolbar';
import { useBankSaveWithConsent } from './useBankSaveWithConsent';
import { MqrConsentIframeDialog } from './MqrConsentIframeDialog';
import { useTranslation } from '../../../shared/language';
import {
  BankCodeOption,
  getBankOptionsByPaymentProvider,
  getPaymentProviderOptions,
  mapBankItemToForm,
  mapBankListItem,
  PaymentBankConfig,
} from './constants';

export const List = () => {
  const {t} = useTranslation();
  const header = t('catalog.beneficiaryBank');
  const dataKey = 'id';
  const [paymentBankConfigs, setPaymentBankConfigs] = useState<
    PaymentBankConfig[]
  >([]);
  const [formBankOptions, setFormBankOptions] = useState<BankCodeOption[]>([]);

  useEffect(() => {
    Service.getPaymentBankConfig().then((configs) => {
      setPaymentBankConfigs(configs);
    });
  }, []);

  const paymentProviderOptions = useMemo(
    () => getPaymentProviderOptions(paymentBankConfigs),
    [paymentBankConfigs],
  );

  const defaultPaymentProvider = paymentProviderOptions[0]?.value;

  const allBankOptions = useMemo(() => {
    const uniqueBanks = Array.from(
      new Set(paymentBankConfigs.map((item) => item.bankCode).filter(Boolean)),
    );
    return uniqueBanks.map((value) => ({ value, label: value }));
  }, [paymentBankConfigs]);

  const defaultBankCode =
    getBankOptionsByPaymentProvider(paymentBankConfigs, defaultPaymentProvider)[0]
      ?.value ?? '';

  const requiredParams = useMemo(
    () => (paymentBankConfigs.length > 0 ? {} : null),
    [paymentBankConfigs.length],
  );

  const { createItemWithConsent, updateItemWithConsent, consentIframeDialog } =
    useBankSaveWithConsent(paymentBankConfigs);

  const { render: renderDataTable, refreshLazyData, reloadLazyData, selectedItems } = useDataTable({
    header,
    dataKey,
    requiredParams,
    columns: [
      {
        field: 'paymentProvider',
        header: t('catalog.paymentProductType'),
        width: 160,
        matchMode: 'equals',
        filterType: 'dropdown',
        filterOptions: paymentProviderOptions,
        dataType: 'fromFilterOptions',
      },
      {
        field: 'code',
        header: t('catalog.beneficiaryBank'),
        width: 160,
        matchMode: 'equals',
        filterType: 'dropdown',
        filterOptions: allBankOptions,
        dataType: 'fromFilterOptions',
      },
      { field: 'bankAccount', header: t('catalog.accountNumber'), width: 200, matchMode: 'contains' },
      { field: 'accountName', header: t('catalog.accountName'), minWidth: 280, matchMode: 'contains' },
      {
        field: 'isDefault',
        header: t('catalog.primaryBeneficiaryAccount'),
        width: 180,
        matchMode: 'equals',
        filterType: 'boolean',
        dataType: 'boolean',
      },
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      return Service.getList({
        ...params,
        ...(params.paymentProvider !== undefined && params.paymentProvider !== null
          ? {}
          : defaultPaymentProvider !== undefined
            ? { paymentProvider: defaultPaymentProvider }
            : {}),
      }).then((response) => ({
        ...response,
        items: (response.items ?? []).map(mapBankListItem),
      }));
    }
  });

  const { render: renderDialogCrup, create, update, clone, getValue, setValue, touched } = useDialogCrup({
    header,
    dataKey,
    width: '40rem',
    defaultValue: {
      paymentProvider: defaultPaymentProvider,
      code: defaultBankCode,
      isDefault: false,
    },
    fields: [
      {
        field: 'paymentProvider',
        header: t('catalog.paymentProductType'),
        required: true,
        type: 'Dropdown',
        DropdownProps: { options: paymentProviderOptions },
        disabled: 'onlyUpdate',
        className: 'md:col-12',
      },
      {
        field: 'code',
        header: t('catalog.beneficiaryBank'),
        required: true,
        type: 'Dropdown',
        DropdownProps: { options: formBankOptions, filter: true },
        disabled: 'onlyUpdate',
        className: 'md:col-12',
      },
      { field: 'bankAccount', header: t('catalog.accountNumber'), required: true, disabled: 'onlyUpdate', className: 'md:col-12' },
      { field: 'accountName', header: t('catalog.accountName'), required: true, className: 'md:col-12' },
      { field: 'isDefault', header: t('catalog.primaryBeneficiaryAccount'), type: 'Checkbox', className: 'md:col-12' },
    ],
    createItem: (item) => {
      return createItemWithConsent(item);
    },
    updateItem: (id, item) => {
      return updateItemWithConsent(id, item);
    },
    refreshLazyData
  });

  const syncFormBankOptions = useCallback(
    (paymentProvider?: number, selectedCode?: string) => {
      setFormBankOptions(
        getBankOptionsByPaymentProvider(
          paymentBankConfigs,
          paymentProvider,
          selectedCode,
        ),
      );
    },
    [paymentBankConfigs],
  );

  const handleCreate = useCallback(() => {
    syncFormBankOptions(defaultPaymentProvider);
    create();
  }, [create, defaultPaymentProvider, syncFormBankOptions]);

  const handleUpdate = useCallback(
    (item: Record<string, unknown>) => {
      const formValue = mapBankItemToForm(item);
      syncFormBankOptions(formValue.paymentProvider, formValue.code);
      update({ ...item, ...formValue });
    },
    [syncFormBankOptions, update],
  );

  const handleClone = useCallback(
    (item: Record<string, unknown>) => {
      const formValue = mapBankItemToForm(item);
      syncFormBankOptions(formValue.paymentProvider, formValue.code);
      clone({ ...item, ...formValue });
    },
    [clone, syncFormBankOptions],
  );

  const { paymentProvider, code, inputChange } = getValue();

  useEffect(() => {
    syncFormBankOptions(paymentProvider, code);
  }, [paymentProvider, code, syncFormBankOptions]);

  useEffect(() => {
    if (!touched || inputChange !== 'paymentProvider') {
      return;
    }

    const nextBankOptions = getBankOptionsByPaymentProvider(
      paymentBankConfigs,
      paymentProvider,
    );
    const currentValue = getValue();
    const nextCode = nextBankOptions.some((item) => item.value === currentValue.code)
      ? currentValue.code
      : nextBankOptions[0]?.value ?? '';

    if (currentValue.code !== nextCode) {
      setValue({ ...currentValue, code: nextCode });
    }
  }, [paymentProvider, inputChange, paymentBankConfigs, touched, getValue, setValue]);

  const { renderToolbar } = useToolbar({
    dataKey,
    create: handleCreate,
    update: handleUpdate,
    clone: handleClone,
    reloadLazyData,
    selectedItems,
    deleteItem(id: string): Promise<any> {
      return Service.delete(id);
    },
  });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {renderDialogCrup()}
      <MqrConsentIframeDialog {...consentIframeDialog} />
    </Fragment>
  )
}
